import { format, parse } from "date-fns";

type OperatingHour = {
  day: string; // Example: "Monday", "Tuesday"
  open_time: string; // Example: "11:00:00"
  close_time: string; // Example: "21:00:00"
};

const formatTime = (time: string): string => {
  const parsedTime = parse(time, "HH:mm:ss", new Date()); // Parse the time
  return format(parsedTime, "hh:mm a"); // Format as "hh:mm AM/PM"
};

const getMinutesFromTimeString = (timeString: string): number => {
  const parsed = parse(timeString, "hh:mm a", new Date());
  return parsed.getHours() * 60 + parsed.getMinutes();
};

const sortTimeRanges = (timeRanges: string): string => {
  return timeRanges
    .split(", ")
    .sort((a, b) => {
      const timeA = a.split(" - ")[0]; // Get start time of first range
      const timeB = b.split(" - ")[0]; // Get start time of second range
      return getMinutesFromTimeString(timeA) - getMinutesFromTimeString(timeB);
    })
    .join(", ");
};

const mergeOperatingHours = (
  hours: OperatingHour[],
): Record<string, string> => {
  const mergedHours: Record<string, string> = {};

  hours.forEach((hour) => {
    if (!mergedHours[hour.day]) {
      mergedHours[hour.day] = `${formatTime(hour.open_time)} - ${formatTime(
        hour.close_time,
      )}`;
    } else {
      mergedHours[hour.day] += `, ${formatTime(hour.open_time)} - ${formatTime(
        hour.close_time,
      )}`;
    }
  });

  // Sort time ranges for each day
  Object.keys(mergedHours).forEach((day) => {
    mergedHours[day] = sortTimeRanges(mergedHours[day]);
  });

  return mergedHours;
};

const getCurrentDayOperatingHours = (
  operatingHours: OperatingHour[],
): string => {
  const today = format(new Date(), "EEEE"); // Get current day as "Monday", "Tuesday", etc.
  const mergedHours = mergeOperatingHours(operatingHours); // Use the existing merge logic

  // Check if the store is closed
  if (!mergedHours[today]) {
    return "Closed";
  }

  return mergedHours[today];
};

export { mergeOperatingHours, getCurrentDayOperatingHours };
